import posthog from 'posthog-js';

import {
  getConsentUpdatedProperties,
  parseCookieYesConsent,
  resolveConsentMethod,
  type ConsentMethod,
  type CookieYesConsent,
} from '@/lib/cookieyes';

export type PostHogEnvironment = 'development' | 'homolog' | 'production';

export const getPostHogEnvironment = (): PostHogEnvironment => {
  const envVar = import.meta.env.VITE_ENV;

  if (
    envVar === 'staging' ||
    envVar === 'stg' ||
    envVar === 'homolog' ||
    envVar === 'hml'
  ) {
    return 'homolog';
  }

  if (envVar === 'production' || envVar === 'prod') {
    return 'production';
  }

  return 'development';
};

const getTracingHosts = (): string[] => {
  const hosts = new Set<string>(['localhost']);

  if (typeof window !== 'undefined') {
    hosts.add(window.location.hostname);
  }

  const apiUrl = import.meta.env.VITE_API_URL;
  if (apiUrl) {
    try {
      hosts.add(new URL(apiUrl).hostname);
    } catch {
      void 0;
    }
  }

  return Array.from(hosts);
};

const POSTHOG_UI_HOSTS_TO_INGEST: Record<string, string> = {
  'app.posthog.com': 'https://us.i.posthog.com',
  'us.posthog.com': 'https://us.i.posthog.com',
  'eu.posthog.com': 'https://eu.i.posthog.com',
};

export const resolvePostHogApiHost = (host?: string): string | undefined => {
  const raw = String(host ?? '').trim().replace(/\/$/, '');
  if (!raw) return undefined;

  try {
    const url = new URL(raw.includes('://') ? raw : `https://${raw}`);
    const ingest = POSTHOG_UI_HOSTS_TO_INGEST[url.hostname];
    if (ingest) return ingest;
    return `${url.protocol}//${url.host}`;
  } catch {
    return undefined;
  }
};

const resolvePostHogUiHost = (apiHost: string): string => {
  if (apiHost.includes('eu.i.posthog.com') || apiHost.includes('eu.posthog.com')) {
    return 'https://eu.posthog.com';
  }
  return 'https://us.posthog.com';
};

const isLoaded = (): boolean =>
  Boolean((posthog as { __loaded?: boolean }).__loaded);

export const isPostHogCapturing = (): boolean => {
  if (!isLoaded()) return false;
  if (typeof posthog.has_opted_out_capturing === 'function') {
    return !posthog.has_opted_out_capturing();
  }
  return true;
};

const REPORT_ID_PATH =
  /\/reports\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i;

export const getReportIdFromLocation = (): string | undefined => {
  if (typeof window === 'undefined') return undefined;
  return window.location.pathname.match(REPORT_ID_PATH)?.[1];
};

const registerEnvironmentProperty = (): void => {
  const reportId = getReportIdFromLocation();
  posthog.register({
    environment: getPostHogEnvironment(),
    ...(reportId ? { report_id: reportId } : {}),
  });
};

export const initPostHog = (): boolean => {
  if (typeof window === 'undefined') return false;

  const token = import.meta.env.VITE_POSTHOG_PROJECT_TOKEN;
  const apiHost =
    resolvePostHogApiHost(import.meta.env.VITE_POSTHOG_HOST) ||
    'https://us.i.posthog.com';

  if (!token) {
    console.warn('PostHog project token not configured');
    return false;
  }

  if (!isLoaded()) {
    posthog.init(token, {
      api_host: apiHost,
      ui_host: resolvePostHogUiHost(apiHost),
      autocapture: false,
      capture_pageview: false,
      capture_pageleave: true,
      opt_out_capturing_by_default: true,
      persistence: 'localStorage+cookie',
      person_profiles: 'identified_only',
      session_recording: {
        maskAllInputs: true,
      },
      tracing_headers: getTracingHosts(),
      loaded: () => {
        flushPendingConsentUpdated();
      },
    });
  }

  const reportId = getReportIdFromLocation();
  if (reportId) {
    posthog.register({ report_id: reportId });
  }

  if (typeof posthog.opt_in_capturing === 'function') {
    posthog.opt_in_capturing();
  }

  if (isLoaded()) {
    registerEnvironmentProperty();
    window.dispatchEvent(new Event('posthog_capturing_enabled'));
    flushPendingConsentUpdated();
  }

  return isLoaded();
};

export const revokePostHogCapturing = (): void => {
  flushPendingConsentUpdated();
  if (!isLoaded()) return;
  posthog.opt_out_capturing();
  posthog.clear_opt_in_out_capturing();
  posthog.opt_out_capturing();
};

type PendingConsentUpdated = {
  consent: CookieYesConsent;
  consentMethod?: ConsentMethod;
  hadPreviousDecision: boolean;
};

let pendingConsentUpdated: PendingConsentUpdated | null = null;

const emitConsentUpdated = (
  consent: CookieYesConsent,
  consentMethod: ConsentMethod | undefined,
  hadPreviousDecision: boolean
): void => {
  const method =
    consentMethod ?? resolveConsentMethod(consent, hadPreviousDecision);

  const reportId =
    typeof posthog.get_property === 'function'
      ? posthog.get_property('report_id')
      : undefined;
  const schemaVersion =
    typeof posthog.get_property === 'function'
      ? posthog.get_property('schema_version')
      : undefined;

  posthog.capture(
    'consent_updated',
    {
      ...getConsentUpdatedProperties(consent, method),
      ...(reportId ? { report_id: reportId } : {}),
      ...(schemaVersion ? { schema_version: schemaVersion } : {}),
      environment: getPostHogEnvironment(),
    },
    { transport: 'sendBeacon' }
  );
};

export const flushPendingConsentUpdated = (): void => {
  if (!pendingConsentUpdated || !isLoaded() || !isPostHogCapturing()) return;
  const pending = pendingConsentUpdated;
  pendingConsentUpdated = null;
  emitConsentUpdated(pending.consent, pending.consentMethod, pending.hadPreviousDecision);
};

export const captureConsentUpdated = (
  consent: CookieYesConsent = parseCookieYesConsent(),
  consentMethod?: ConsentMethod,
  hadPreviousDecision = false
): void => {
  pendingConsentUpdated = { consent, consentMethod, hadPreviousDecision };
  if (!initPostHog() || !isPostHogCapturing()) return;
  flushPendingConsentUpdated();
};

export { posthog };
